/**
 * UX-only moderator checks. Backend /admin/moderation still returns 403
 * unless the JWT has ROLE_ADMIN or ROLE_MODERATOR.
 */

export function canonicalRole(value) {
  const raw = String(value || '').trim().toUpperCase();
  if (!raw) return '';
  return raw.startsWith('ROLE_') ? raw : `ROLE_${raw}`;
}

export function collectRoleNames(user, token) {
  const names = new Set();
  const push = (value) => {
    const canonical = canonicalRole(value);
    if (canonical) names.add(canonical);
  };

  if (user && typeof user === 'object') {
    push(user.role);
    const authorities = user.authorities;
    if (Array.isArray(authorities)) {
      authorities.forEach((item) => {
        if (typeof item === 'string') push(item);
        else if (item && typeof item === 'object') push(item.name || item.authority);
      });
    } else if (typeof authorities === 'string') {
      authorities.split(',').forEach(push);
    }
  }

  rolesFromJwt(token).forEach(push);
  return Array.from(names);
}

export function rolesFromJwt(token) {
  if (!token || typeof token !== 'string') return [];
  const parts = token.split('.');
  if (parts.length < 2) return [];
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    const auth = payload.auth || payload.authorities || payload.roles;
    if (typeof auth === 'string') {
      return auth.split(',').map((part) => part.trim()).filter(Boolean);
    }
    if (Array.isArray(auth)) {
      return auth
        .map((item) => (typeof item === 'string' ? item : item?.name || item?.authority))
        .filter(Boolean);
    }
  } catch {
    return [];
  }
  return [];
}

export function base64UrlDecode(segment) {
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  if (typeof atob === 'function') {
    return decodeURIComponent(
      Array.from(atob(padded), (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')
    );
  }
  return Buffer.from(padded, 'base64').toString('utf8');
}

export function isModerator(user, token) {
  return collectRoleNames(user, token).some(
    (role) => role === 'ROLE_ADMIN' || role === 'ROLE_MODERATOR'
  );
}

/**
 * @returns {true | { path: string } | { name: string }}
 */
export function resolveModeratorNavigation({ token, user } = {}) {
  if (!token) {
    return { path: '/login' };
  }
  if (!isModerator(user, token)) {
    return { name: 'NotFound' };
  }
  return true;
}
